'use client';

import * as React from 'react';
import { Select as SelectPrimitive } from '@base-ui/react/select';
import Avatar from '@savvy-kit/components/avatar';
import { Button } from '@savvy-kit/components/button';
import { Checkbox } from '@savvy-kit/components/checkbox';
import { cn } from '@savvy-kit/lib/utils';
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';

type SelectProps<Value extends string = string> = Omit<SelectPrimitive.Root.Props<Value>, 'onValueChange'> & {
  onValueChange?: (value: Value) => void;
};

function Select<Value extends string = string>({ onValueChange, ...props }: SelectProps<Value>) {
  return (
    <SelectPrimitive.Root<Value>
      data-slot="select"
      {...props}
      onValueChange={
        onValueChange !== undefined
          ? (value) => {
              if (value !== null) onValueChange(value);
            }
          : undefined
      }
    />
  );
}

function SelectGroup({ ...props }: SelectPrimitive.Group.Props) {
  return <SelectPrimitive.Group data-slot="select-group" {...props} />;
}

function SelectValue({ className, ...props }: SelectPrimitive.Value.Props) {
  return (
    <SelectPrimitive.Value data-slot="select-value" className={cn('flex flex-1 text-left', className)} {...props} />
  );
}

export type SelectTriggerProps = SelectPrimitive.Trigger.Props & {
  readonly size?: 'sm' | 'default';
};

function SelectTrigger({ className, size = 'default', children, render, ...props }: SelectTriggerProps) {
  // When the consumer provides a `render` element, skip kit's heavy trigger styling
  // and the inner div/chevron wrapper -- they want their own element rendered.
  const hasCustomRender = render !== undefined;
  return (
    <SelectPrimitive.Trigger
      data-slot="select-trigger"
      data-size={size}
      render={render}
      className={cn(
        !hasCustomRender &&
          "border-light data-[placeholder]:text-secondary data-[placeholder]:text-sm [&_svg:not([class*='text-'])]:text-secondary aria-invalid:ring-destructive/20 aria-invalid:border-destructive flex min-w-0 w-fit items-center justify-between gap-2 rounded-md border bg-card px-3 py-2 text-left text-sm shadow-xs transition-[color,box-shadow] outline-none disabled:cursor-not-allowed disabled:opacity-50 data-[size=default]:h-10 data-[size=sm]:h-8 [&_[data-slot=select-value]]:min-w-0 [&_[data-slot=select-value]]:flex-1 [&_[data-slot=select-value]]:truncate [&_[data-slot=select-value]]:text-left [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        !hasCustomRender && 'focus-visible:border-primary focus-visible:ring-border-light focus-visible:ring-2',
        className,
      )}
      {...props}
    >
      {hasCustomRender ? (
        children
      ) : (
        <>
          <div className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden [&_[data-slot='avatar']]:!size-5 [&_[data-slot=select-value]]:line-clamp-1">
            {children}
          </div>
          <SelectPrimitive.Icon render={<ChevronDownIcon className="size-4" />} />
        </>
      )}
    </SelectPrimitive.Trigger>
  );
}

function SelectContent({
  className,
  children,
  side = 'bottom',
  sideOffset = 4,
  align = 'center',
  alignOffset = 0,
  alignItemWithTrigger = false,
  positionerClassName,
  ...props
}: SelectPrimitive.Popup.Props &
  Pick<SelectPrimitive.Positioner.Props, 'align' | 'alignOffset' | 'side' | 'sideOffset' | 'alignItemWithTrigger'> & {
    positionerClassName?: string;
  }) {
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Positioner
        data-slot="select-positioner"
        side={side}
        sideOffset={sideOffset}
        align={align}
        alignOffset={alignOffset}
        alignItemWithTrigger={alignItemWithTrigger}
        className={cn('isolate z-50', positionerClassName)}
      >
        <SelectPrimitive.Popup
          data-slot="select-content"
          className={cn(
            'bg-popover text-popover-foreground data-open:animate-in data-closed:animate-out data-closed:fade-out-0 data-open:fade-in-0 data-closed:zoom-out-95 data-open:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 relative z-50 max-h-(--available-height) min-w-[8rem] origin-(--transform-origin) overflow-x-hidden overflow-y-auto rounded-md border border-light shadow-md',
            className,
          )}
          {...props}
        >
          <SelectScrollUpButton />
          <SelectPrimitive.List className="p-1 w-full min-w-[var(--anchor-width)]">{children}</SelectPrimitive.List>
          <SelectScrollDownButton />
        </SelectPrimitive.Popup>
      </SelectPrimitive.Positioner>
    </SelectPrimitive.Portal>
  );
}

function SelectLabel({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot="select-label" className={cn('text-secondary px-2 py-1.5 text-sm', className)} {...props} />;
}

function SelectItem({ className, children, ...props }: SelectPrimitive.Item.Props) {
  return (
    <SelectPrimitive.Item
      data-slot="select-item"
      className={cn(
        "relative flex w-full cursor-default items-center gap-1.5 rounded-md py-1 pr-8 pl-1.5 text-sm outline-hidden select-none focus:bg-accent focus:text-accent-foreground not-data-[variant=destructive]:focus:**:text-accent-foreground data-disabled:pointer-events-none data-disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 *:[span]:last:flex *:[span]:last:items-center *:[span]:last:gap-2",
        className,
      )}
      {...props}
    >
      <SelectPrimitive.ItemText className="flex flex-1 shrink-0 gap-2 whitespace-nowrap">
        {children}
      </SelectPrimitive.ItemText>
      <SelectPrimitive.ItemIndicator
        render={<span className="pointer-events-none absolute right-2 flex size-4 items-center justify-center" />}
      >
        <CheckIcon className="pointer-events-none" />
      </SelectPrimitive.ItemIndicator>
    </SelectPrimitive.Item>
  );
}

export type SelectItemWithAvatarProps = SelectPrimitive.Item.Props & {
  readonly imageUrl?: string | null;
  readonly firstName?: string;
  readonly lastName?: string | null;
  readonly showFallbackIcon?: boolean;
};

function SelectItemWithAvatar({
  imageUrl,
  firstName,
  lastName,
  showFallbackIcon,
  children,
  ...props
}: SelectItemWithAvatarProps) {
  return (
    <SelectItem {...props}>
      <div className="flex items-center gap-2 text-sm">
        <Avatar imageUrl={imageUrl} firstName={firstName} lastName={lastName} showFallbackIcon={showFallbackIcon} />
        {children}
      </div>
    </SelectItem>
  );
}

export type SelectItemWithCheckboxProps = {
  readonly checked: boolean | 'indeterminate';
  readonly onCheckedChange?: (checked: boolean) => void;
  readonly className?: string;
  readonly disabled?: boolean;
  readonly children: React.ReactNode;
};

function SelectItemWithCheckbox({
  checked,
  onCheckedChange,
  className,
  disabled,
  children,
}: SelectItemWithCheckboxProps) {
  return (
    <div
      role="option"
      aria-selected={!!checked}
      tabIndex={disabled ? undefined : 0}
      data-slot="select-item-checkbox"
      data-disabled={disabled ? '' : undefined}
      className={cn(
        'flex w-full cursor-pointer items-start gap-3 rounded-sm py-1.5 px-2 text-sm select-none outline-none',
        'hover:bg-component-hover focus-visible:bg-component-hover',
        'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
        className,
      )}
      onClick={() => {
        if (!disabled) {
          onCheckedChange?.(checked !== true);
        }
      }}
      onKeyDown={(e) => {
        if (disabled) return;
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          onCheckedChange?.(checked !== true);
        }
      }}
    >
      <Checkbox checked={checked} disabled={disabled} tabIndex={-1} className="pointer-events-none mt-0.5 gap-0" />
      <div className="flex-1 min-w-0">{children}</div>
    </div>
  );
}

type SelectActionBarProps = {
  readonly allSelected: boolean;
  readonly onSelectAllToggle: () => void;
  readonly onDone: () => void;
  readonly doneDisabled?: boolean;
  readonly className?: string;
  /**
   * Hide the select-all / deselect-all toggle. Set this when the list is
   * filtered (e.g. a search box narrows the visible options), since "select
   * all" is ambiguous when not every option is on screen.
   */
  readonly showSelectAll?: boolean;
};

function SelectActionBar({
  allSelected,
  onSelectAllToggle,
  onDone,
  doneDisabled = false,
  className,
  showSelectAll = true,
}: SelectActionBarProps) {
  return (
    <div
      data-slot="select-action-bar"
      className={cn(
        'flex items-center justify-between rounded-b-md border-t border-border-light bg-muted px-3 py-2',
        className,
      )}
    >
      {showSelectAll ? (
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            onSelectAllToggle();
            e.currentTarget.blur();
          }}
        >
          {allSelected ? 'Deselect all' : 'Select all'}
        </Button>
      ) : (
        <span />
      )}
      <Button size="sm" onClick={onDone} disabled={doneDisabled}>
        Done
      </Button>
    </div>
  );
}

function SelectSeparator({ className, ...props }: SelectPrimitive.Separator.Props) {
  return (
    <SelectPrimitive.Separator
      data-slot="select-separator"
      className={cn('bg-border-light pointer-events-none -mx-1 my-1 h-px', className)}
      {...props}
    />
  );
}

function SelectScrollUpButton({ className, ...props }: React.ComponentProps<typeof SelectPrimitive.ScrollUpArrow>) {
  return (
    <SelectPrimitive.ScrollUpArrow
      data-slot="select-scroll-up-button"
      className={cn('flex cursor-default items-center justify-center py-1', className)}
      {...props}
    >
      <ChevronUpIcon className="size-4" />
    </SelectPrimitive.ScrollUpArrow>
  );
}

function SelectScrollDownButton({ className, ...props }: React.ComponentProps<typeof SelectPrimitive.ScrollDownArrow>) {
  return (
    <SelectPrimitive.ScrollDownArrow
      data-slot="select-scroll-down-button"
      className={cn('flex cursor-default items-center justify-center py-1', className)}
      {...props}
    >
      <ChevronDownIcon className="size-4" />
    </SelectPrimitive.ScrollDownArrow>
  );
}

export {
  Select,
  SelectActionBar,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectItemWithAvatar,
  SelectItemWithCheckbox,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
};
